import { useMutation } from '@apollo/client';
import React, { useState, useContext, useEffect } from 'react';
import { useUser } from '~/src/utils/user';
import GENERATE_TOKEN from '~src/queries/codegen/generateToken';
import { useMessageHandler } from './useSubscriptions';

const RECONNECT_TIME = Number.parseInt(process.env.REACT_APP_TTL_WS || '10000');
const SAFE_CLOSE = 'safeClose';
const DOMAIN = process.env.PORTAL_GRAPHQL_URI_WS;

export const getProjectWS = (token: string) => {
  return new WebSocket(`${DOMAIN}?access_token=${token}`);
};
const initialContext: SubscriptionConnectionContextProps = {
  ws: null,
  ready: false,
};

export interface SubscriptionConnectionContextProps {
  ws: WebSocket | null;
  ready: boolean;
}

export const useWsSubscriptionContext = () => useContext(SubscriptionConnectionContext);

export const SubscriptionConnectionContext = React.createContext<SubscriptionConnectionContextProps>(initialContext);

export const SubscriptionConnectionContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [ws, setWs] = useState<WebSocket | null>(initialContext.ws);
  const [ready, setReady] = useState<boolean>(initialContext.ready);
  const { currentUser } = useUser();

  const [getToken] = useMutation(GENERATE_TOKEN, {
    fetchPolicy: 'no-cache',
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onError: () => {
      setTimeout(() => getToken(), RECONNECT_TIME);
    },
    onCompleted: (data) => {
      const ws = getProjectWS(data?.generateSingleUseToken?.token);
      ws.onopen = () => setReady(true);
      ws.onclose = (e) => {
        setReady(false);
        ws.close();
        if (e.reason != SAFE_CLOSE) setTimeout(() => getToken(), RECONNECT_TIME);
        else setWs(null);
      };

      setWs(ws);
    },
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (currentUser?.id) {
      getToken();
    }
  }, [currentUser?.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useMessageHandler(ws);

  return (
    <SubscriptionConnectionContext.Provider
      value={{
        ws,
        ready,
      }}
    >
      {children}
    </SubscriptionConnectionContext.Provider>
  );
};
